import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './GuardedCheckout';

// -----------------------------------------------------------------------------
// TopupSection
// Displays four one-time additional credit options.
// - Maximum 1 additional credit purchase per billing cycle
// - If already purchased, disables all and shows upgrade prompt
// - Shows upgrade interception modal before any top-up purchase
// - Processes payment through Stripe checkout
// -----------------------------------------------------------------------------

// Section 5 - Top-up interception: best upgrade comparison for each topup key.
// topupPrice MUST match the server-authoritative TOPUP_SPECS in backend/routes/stripe_webhook.py --
// topup_120 was shown here as $49.99 while the server charged $60.00, a $10 under-display on the
// screen that exists to help a customer compare the top-up against an upgrade.
// NOTE: topup_200 and topup_350 below are INERT -- no such topup key exists in TOPUP_SPECS, so this
// map can never be keyed by them. Retained; they render nothing. Their upgradePrice values
// are kept in step with the live plan prices anyway, so a future key never revives a stale one.
const UPGRADE_COMPARISON = {
  topup_60:  { topupLabel: '60 credits',  topupPrice: '$29.99', upgradePlan: 'Architect', upgradeCredits: '220 credits/month', upgradePrice: '$25/mo' },
  topup_120: { topupLabel: '120 credits', topupPrice: '$60.00', upgradePlan: 'Visionary', upgradeCredits: '420 credits/month', upgradePrice: '$40/mo' },
  topup_200: { topupLabel: '200 credits', topupPrice: '$74.99', upgradePlan: 'Creator Pro', upgradeCredits: '700 credits/month', upgradePrice: '$65/mo' },
  topup_350: { topupLabel: '350 credits', topupPrice: '$99.99', upgradePlan: 'Commercial', upgradeCredits: '1100 credits/month', upgradePrice: '$130/mo' },
};

function InterceptionModal({ topup_key, opt, onUpgrade, onContinue, onClose }) {
  const cmp = UPGRADE_COMPARISON[topup_key] || {};
  return (
    <div style={im.overlay}>
      <div style={im.modal}>
        <div style={im.heading}>You are about to pay more per credit.</div>
        <div style={im.sub}>Compare your options before continuing:</div>

        <div style={im.compareRow}>
          <div style={im.compareBox}>
            <div style={im.compareLabel}>Top-up</div>
            <div style={im.compareValue}>{cmp.topupLabel || `${opt.specs} credits`}</div>
            <div style={im.comparePrice}>{cmp.topupPrice || `$${opt.price_usd != null ? opt.price_usd.toFixed(2) : '-'}`}</div>
            <div style={im.compareNote}>One-time. No monthly reset.</div>
          </div>
          <div style={im.vs}>VS</div>
          <div style={{ ...im.compareBox, ...im.compareBoxHighlight }}>
            <div style={im.compareLabel}>{cmp.upgradePlan || 'Upgrade'} plan</div>
            <div style={im.compareValue}>{cmp.upgradeCredits || 'More credits/month'}</div>
            <div style={{ ...im.comparePrice, color: '#7c3aed' }}>{cmp.upgradePrice || 'Better value'}</div>
            <div style={im.compareNote}>Resets monthly. Better per-credit value.</div>
          </div>
        </div>

        <div style={im.upgradeMsg}>Upgrade instead and get more credits for less</div>

        <button style={im.primaryBtn} onClick={onUpgrade}>
          Upgrade Plan
        </button>
        <button style={im.secondaryBtn} onClick={onContinue}>
          Continue with Top-up
        </button>
        <button style={im.closeBtn} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

export default function TopupSection({ onPurchaseComplete, compact = false }) {
  // Shared consent controller. Inert unless the SERVER refuses the checkout pending an
  // immediate-supply acknowledgement; then it renders the server's own wording and retries.
  const consentCheckout = useGuardedCheckout();
  const [options,    setOptions]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [error,      setError]      = useState('');
  const [intercepting, setIntercepting] = useState(null); // { topup_key, opt }

  const loadOptions = useCallback(() => {
    setLoading(true);
    API.get('/v1/topup/options')
      .then(r => setOptions(r.data.options || []))
      .catch(() => setError('Unable to load additional credit options.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const handlePurchaseClick = (topup_key, opt) => {
    // Section 5 - Show interception modal before any purchase
    setIntercepting({ topup_key, opt });
  };

  const executePurchase = async (topup_key) => {
    setIntercepting(null);
    setPurchasing(topup_key);
    setError('');
    try {
      const r = await consentCheckout.start(() => API.post('/v1/topup/checkout', { topup_key }), { purchaseType: 'topup', intendedSelector: topup_key });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      const msg = err.response?.data?.error || 'Payment failed - no credits were used. Try again or choose another payment method.';
      setError(msg);
      setPurchasing(null);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 14 }}>
      Loading additional credit options...
    </div>
  );

  const alreadyUsed = options.some(o => o.already_purchased);

  return (
    <div>
      <GuardedCheckoutConsent checkout={consentCheckout} />
      {/* Section 5 - Interception modal */}
      {intercepting && (
        <InterceptionModal
          topup_key={intercepting.topup_key}
          opt={intercepting.opt}
          onUpgrade={() => { setIntercepting(null); window.location.href = '/manage'; }}
          onContinue={() => executePurchase(intercepting.topup_key)}
          onClose={() => setIntercepting(null)}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{
          margin: 0,
          fontSize: compact ? 14 : 16,
          fontWeight: 700, color: '#1f2937',
          letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>
          Additional Credits
        </h3>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6b7280' }}>
          One additional credit purchase per billing cycle. Subscription credits are always used first.
        </p>

        {/* Already used notice */}
        {alreadyUsed && (
          <div style={{
            marginTop: 12, padding: '12px 16px',
            background: '#f3f0ff', borderRadius: 10,
            border: '1px solid #ddd6fe', fontSize: 13, color: '#5b21b6',
          }}>
            <strong>Additional credits already purchased this cycle.</strong> Your next purchase will be available at your next billing reset.
            To get more generations now, upgrade your plan.
          </div>
        )}

        {/* Value nudge banner */}
        {!alreadyUsed && (
          <div style={{
            marginTop: 10, padding: '10px 14px',
            background: '#fef3c7', borderRadius: 8,
            border: '1px solid #fbbf24', fontSize: 13, color: '#92400e',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>&#x1F4A1;</span>
            <span>
              <strong>Better value:</strong> Additional credits cost more per generation than a subscription.{' '}
              <a href="/manage" style={{ color: '#92400e', textDecoration: 'underline', fontWeight: 600 }}>
                Compare plans
              </a>
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', background: '#fee2e2', borderRadius: 8,
          marginBottom: 12, fontSize: 13, color: '#991b1b', fontWeight: 500,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Payment failed - no credits were used</div>
          <div>{error}</div>
          <div style={{ marginTop: 4, fontSize: 12 }}>Try again or choose another payment method.</div>
        </div>
      )}

      {/* Credit Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {options.map(opt => {
          const isDisabled = alreadyUsed || opt.already_purchased;
          return (
            <div key={opt.topup_key} style={{
              border: `2px solid ${isDisabled ? '#e5e7eb' : '#7c3aed'}`,
              borderRadius: 12,
              padding: compact ? 16 : 20,
              opacity: isDisabled ? 0.55 : 1,
              position: 'relative',
              background: 'white',
              transition: 'box-shadow 0.2s',
            }}>
              {isDisabled && (
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  background: '#e5e7eb', borderRadius: 20,
                  padding: '2px 10px', fontSize: 11,
                  color: '#6b7280', fontWeight: 600,
                }}>
                  {opt.already_purchased ? 'Purchased' : 'Unavailable'}
                </div>
              )}

              <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {opt.label}
              </div>

              {/* Section 6 - Credit clarity */}
              <div style={{ fontSize: compact ? 22 : 28, fontWeight: 800, color: '#1f2937', lineHeight: 1.1 }}>
                +{opt.specs} credits
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                Generates approximately {Math.round(opt.specs * 0.5)}-{opt.specs} images
              </div>

              <div style={{ fontSize: compact ? 18 : 22, fontWeight: 700, color: '#7c3aed', margin: '8px 0 4px' }}>
                ${opt.price_usd != null ? opt.price_usd.toFixed(2) : (opt.price_aud != null ? opt.price_aud.toFixed(2) : '-')}
              </div>

              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
                ${opt.price_per_spec != null ? opt.price_per_spec.toFixed(2) : '-'}/credit - one per billing cycle
              </div>

              <button
                disabled={isDisabled || purchasing === opt.topup_key}
                onClick={() => handlePurchaseClick(opt.topup_key, opt)}
                style={{
                  width: '100%',
                  padding: compact ? '8px 0' : '11px 0',
                  background: isDisabled
                    ? '#e5e7eb'
                    : purchasing === opt.topup_key
                      ? '#a78bfa'
                      : '#7c3aed',
                  color: isDisabled ? '#9ca3af' : 'white',
                  border: 'none', borderRadius: 8,
                  fontWeight: 700, fontSize: 14,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {purchasing === opt.topup_key
                  ? 'Redirecting...'
                  : isDisabled
                    ? 'Not Available'
                    : 'Continue to Secure Checkout'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Upgrade prompt when credits already used */}
      {alreadyUsed && (
        <div style={{
          marginTop: 20, padding: '16px 20px',
          background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(168,85,247,0.1))',
          border: '1px solid rgba(139,92,246,0.3)',
          borderRadius: 12, textAlign: 'center',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937', marginBottom: 6 }}>
            Need more generations now?
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Upgrade your VISINVOKE plan for a higher monthly allowance with no cycle restrictions.
          </div>
          <a href="/manage" style={{
            display: 'inline-block',
            background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
            color: '#fff', textDecoration: 'none',
            borderRadius: 8, padding: '10px 24px',
            fontWeight: 700, fontSize: 14,
          }}>
            Upgrade Plan
          </a>
        </div>
      )}
    </div>
  );
}

// Interception modal styles
const im = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#fff', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' },
  heading: { fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 },
  sub: { fontSize: 14, color: '#6b7280', marginBottom: 24 },
  compareRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  compareBox: { flex: 1, background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: 12, padding: '14px 12px' },
  compareBoxHighlight: { background: '#f5f3ff', border: '2px solid #8b5cf6' },
  compareLabel: { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  compareValue: { fontSize: 15, fontWeight: 800, color: '#1f2937', marginBottom: 4 },
  comparePrice: { fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 4 },
  compareNote: { fontSize: 11, color: '#9ca3af' },
  vs: { fontSize: 13, fontWeight: 800, color: '#9ca3af', flexShrink: 0 },
  upgradeMsg: { fontSize: 14, fontWeight: 700, color: '#7c3aed', marginBottom: 20 },
  primaryBtn: { width: '100%', padding: '13px 0', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 15, cursor: 'pointer', marginBottom: 10 },
  secondaryBtn: { width: '100%', padding: '10px 0', background: 'transparent', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: 8 },
  closeBtn: { background: 'none', border: 'none', color: '#d1d5db', fontSize: 12, cursor: 'pointer', padding: '4px 0' },
};
